import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { fetchQuotes, getImageSrc } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';


const QuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const scrollerRef = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchQuotes();
        setQuotes(data);
      } catch (error) {
        console.error('Error fetching quotes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (quotes.length > 0) {
      addAnimation();
    }
  }, [quotes]);

  const addAnimation = () => {
    if (containerRef.current && scrollerRef.current) {
      const scrollerContent = Array.from(scrollerRef.current.children);

      scrollerContent.forEach((item) => {
        const duplicatedItem = item.cloneNode(true);
        if (scrollerRef.current) {
          scrollerRef.current.appendChild(duplicatedItem);
        }
      });

      // Set direction to left (right to left movement)
      containerRef.current.style.setProperty("--animation-direction", "forwards");
      // Increase speed slightly (15s instead of 20s)
      containerRef.current.style.setProperty("--animation-duration", "25s");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (quotes.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-xl text-gray-600 font-marathi">
          {language === 'MR' ? 'स्फूतविचार उपलब्ध नाहीत' : 'No quotes available'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white py-4 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header section with title and subtitle */}
        <div className="flex items-center mb-8">
          <div className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800"></div>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-red-800 mr-4">
              {language === 'MR' ? 'स्फूतविचार' : 'QUOTES'}
            </h1>
            <p className="text-sm text-gray-600 mt-2 italic">
              {language === 'MR' ? 'प्रेरणादायी विचार आणि जीवन मूल्ये' : 'Inspirational thoughts and life values'}
            </p>
          </div>
          <div className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"></div>
        </div>

        {/* Keep original quotes design */}
        <div
          ref={containerRef}
          className="scroller relative z-20 w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_20%,white_80%,transparent)]"
        >
          <ul
            ref={scrollerRef}
            className="flex w-max min-w-full shrink-0 flex-nowrap gap-6 py-4 animate-scroll hover:[animation-play-state:paused]"
          >
            {quotes.map((quote, idx) => (
              <li
                key={`${quote._id}-${idx}`}
                className="relative w-[350px] max-w-full shrink-0 rounded-2xl border border-red-200 bg-gradient-to-r from-red-50 to-white shadow-md px-8 py-6"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 overflow-hidden mr-4 flex items-center justify-center">
                      {quote.image ? (
                        <img loading="lazy" 
                          src={getImageSrc(quote.image)} 
                          alt={quote.author}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-red-800 text-xl font-bold">
                          {quote.author.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-red-900">{quote.author}</h3>
                      <p className="text-sm text-gray-600">
                        {new Date(quote.date).toLocaleDateString(language === 'MR' ? 'mr-IN' : 'en-US')}
                      </p>
                    </div>
                  </div>
                  <blockquote className="text-gray-800 text-lg italic pl-2 border-l-4 border-red-800 flex-grow">
                    <p className="mt-2">{quote.content}</p>
                  </blockquote>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <style jsx global>{`
        @keyframes scroll {
          to {
            transform: translateX(calc(-50% - 1.5rem));
          }
        }
        .animate-scroll {
          animation: scroll var(--animation-duration, 15s) linear infinite;
          animation-direction: var(--animation-direction, forwards);
        }
      `}</style>
    </div>
  );
};

export default QuotesPage;